import React, { useRef } from 'react';
import StyledSection from './styles/StyledSection';
import { EOISectionContent } from './eoi/EOISectionContent';
import {
  EOISectionSuggestionConfirmModal,
  EOISectionWithdrawSuggestionModal,
} from './eoi/EOISectionModals';
import { isEOIAllowedForFundStatus } from './eoi/eoiFundStatusUtils';
import { EOISectionRestricted } from './eoi/EOISectionRestricted';
import { EOISettingsProvider } from './eoi/EOISettingsContext';
import type { EOISectionProps, EOISettings } from './eoi/eoiTypes';
import { useEOISectionScroll } from './eoi/eoiScrollUtils';
import { useEOISectionState } from './eoi/useEOISectionState';

export type { EOISectionProps, EOISettings };

const EOISection: React.FC<EOISectionProps> = (props) => {
  const {
    investor,
    fundOpportunity,
    downloadPDF,
    showDownloadPdf = true,
    printMode = false,
    referrer,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const state = useEOISectionState(props);

  useEOISectionScroll(
    investor?.id,
    investor?.updatedAt,
    state.updateMode,
    state.withdrawMode,
    rootRef,
  );

  const eoiAllowed = isEOIAllowedForFundStatus(fundOpportunity.status);

  if (!printMode && !referrer && (!eoiAllowed || investor.status !== 'LEAD')) {
    if (!eoiAllowed) {
      return <EOISectionRestricted fundOpportunity={fundOpportunity} printMode={printMode} />;
    }
    return null;
  }

  if (referrer && !printMode && !eoiAllowed) {
    return <EOISectionRestricted fundOpportunity={fundOpportunity} printMode={printMode} />;
  }

  return (
    <EOISettingsProvider value={{ downloadPDF, printMode, showDownloadPdf }}>
      <StyledSection
        ref={rootRef}
        tone="tone1"
        additionalClassName="eoi-section react-EOISection keep-together-for-print"
        mt="1.875rem"
        withBorder={printMode}
        data-investor-id={investor.id}
      >
        <EOISectionContent {...props} state={state} printMode={printMode} />
      </StyledSection>

      <EOISectionSuggestionConfirmModal
        open={state.showSuggestionConfirm}
        investor={investor}
        pendingSuggestion={state.pendingSuggestion}
        hasSubmitted={state.hasSubmitted}
        suggestionComment={state.suggestionComment}
        suggesting={state.suggesting}
        onClose={state.handleCancelSuggestion}
        onConfirm={state.handleConfirmSuggestion}
        onCommentChange={state.setSuggestionComment}
      />

      <EOISectionWithdrawSuggestionModal
        open={state.showWithdrawConfirm}
        investor={investor}
        withdrawing={state.withdrawing}
        onClose={state.handleCancelWithdrawal}
        onConfirm={state.handleConfirmWithdrawal}
      />
    </EOISettingsProvider>
  );
};

export default EOISection;
